import { useEffect, useState } from 'react';
import axios from '../utils/axiosConfig';
import { DEFAULT_JOURNAL_SETTINGS, JournalSettings } from '../config/journalDefaults';
import { readPrerenderedPageData } from '../utils/prerenderedPageData';

// One request per page load, shared by every component that needs the settings (the
// home page alone used to fetch them four times).
let settingsCache: JournalSettings | null = null;
let settingsRequest: Promise<JournalSettings | null> | null = null;

/** The settings as last fetched, or as baked into a prerendered page; null if neither. */
function knownSettings(): JournalSettings | null {
  if (settingsCache) return settingsCache;
  const baked = readPrerenderedPageData()?.settings;
  return baked ? { ...DEFAULT_JOURNAL_SETTINGS, ...baked } as JournalSettings : null;
}

/** Fetches GET /api/journal/settings once; resolves to null (and allows a retry) on failure. */
export function loadJournalSettings(): Promise<JournalSettings | null> {
  if (!settingsRequest) {
    settingsRequest = axios.get('/api/journal/settings')
      .then((res) => (settingsCache = { ...DEFAULT_JOURNAL_SETTINGS, ...res.data }))
      .catch(() => {
        settingsRequest = null;
        return null;
      });
  }
  return settingsRequest;
}

/**
 * Fetches the public GET /api/journal/settings endpoint, falling back to
 * DEFAULT_JOURNAL_SETTINGS (neutral placeholders) while loading or if the
 * request fails, so pages never render blank/undefined branding fields.
 * On a prerendered page the settings baked into the page are used until the
 * live ones arrive, so the first render already shows real values.
 *
 * `settingsLoading` lets callers avoid rendering the neutral-placeholder
 * values (e.g. "Your Journal Name") as if they were real content -- show a
 * skeleton/neutral state instead while this is true.
 */
export function useJournalSettings(): JournalSettings & { settingsLoading: boolean } {
  const [settings, setSettings] = useState<JournalSettings | null>(knownSettings);
  const [settingsLoading, setSettingsLoading] = useState(() => knownSettings() === null);

  useEffect(() => {
    let active = true;
    loadJournalSettings().then((fresh) => {
      if (!active) return;
      if (fresh) setSettings(fresh);
      setSettingsLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  return { ...(settings ?? DEFAULT_JOURNAL_SETTINGS), settingsLoading };
}
